import { defineComponent as _defineComponent } from 'vue'
import { reactive, computed, watch, ref } from 'vue';
import { FieldOptionObj, FormDefinition, FormEvent } from 'ah-common-lib/src/form/interfaces';
import { makeFormModel } from 'ah-common-lib/src/form/helpers';
import { radioField } from 'ah-common-lib/src/form/models';
import { useTradeState } from '../../';
import { useOnBehalfOf } from 'ah-common-lib/src/onBehalfOf/useInjectedOBO';
import { QuotePriceResponse, FundingType, TradeFundingDetails } from 'ah-api-gateways';
import { formatCurrencyValue } from 'ah-common-lib/src/helpers/currency';
import { ValidatedForm } from 'ah-common-lib/src/form/components';
import CollateralTransactionExecutor from 'ah-trades/src/components/collaterals/CollateralTransactionExecutor.vue';
import TradeCollateralCreditFundForm from './TradeCollateralCreditFundForm.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'TradeFundForm',
  props: {
    tradePriceResponse: null,
    tradeFundingDetails: null
  },
  emits: ["update:tradeFundingDetails"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'update:tradeFundingDetails', value: Partial<TradeFundingDetails>): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  tradePriceResponse: QuotePriceResponse;
  tradeFundingDetails?: Partial<TradeFundingDetails>;
};





const fundingOptions: FieldOptionObj[] = [
  { label: 'Fund with Collateral', value: FundingType.COLLATERAL },
  { label: 'Fund with Credit', value: FundingType.CREDIT },
  { label: 'Fund with Both', value: FundingType.COLLATERAL_AND_CREDIT },
];

const hasPostedCollateral = ref(false);

const tradeState = useTradeState();

const onBehalfOf = useOnBehalfOf();

function loadCollaterals() {
  tradeState.store.useWalletsStore().loadCollaterals({ force: false, clientId: onBehalfOf.value?.id });
}

loadCollaterals();

const collaterals = computed(() => {
  const clientId = onBehalfOf.value
    ? onBehalfOf.value.id
    : tradeState.store.useAuthStore().loggedInIdentity?.client?.id;

  if (clientId) {
    return tradeState.store.useWalletsStore().getCollaterals(clientId);
  }
  return null;
});

const currency = computed(() => props.tradePriceResponse.homeCurrency);

const areCollateralsLoading = computed(() =>
  tradeState.store.useWalletsStore().areCollateralsLoading(onBehalfOf.value?.id)
);

const sufficientCollateral = computed(() => {
  if (!collaterals.value || areCollateralsLoading.value) {
    return false;
  }

  return (
    props.tradePriceResponse.homeCurrencyInitialMarginAmount <= 0 ||
    collaterals.value.marginCollateralAvailable >= props.tradePriceResponse.homeCurrencyInitialMarginAmount
  );
});

const fundWithCollateralPossible = computed(() => {
  if (!collaterals.value || areCollateralsLoading.value) {
    return false;
  }

  return (
    sufficientCollateral.value &&
    collaterals.value.initialMarginCollateralAvailable >= props.tradePriceResponse.homeCurrencyInitialMarginAmount
  );
});

const fundWithCreditPossible = computed(() => {
  if (!collaterals.value || areCollateralsLoading.value) {
    return false;
  }

  return (
    sufficientCollateral.value &&
    collaterals.value.initialMarginCreditAvailable >= props.tradePriceResponse.homeCurrencyInitialMarginAmount
  );
});

const fundingFM = reactive<FormDefinition>({
  form: makeFormModel({
    name: 'fundingForm',
    fieldType: 'form',
    fields: [
      radioField('initialMarginFundingType', '', fundingOptions, {
        inline: true,
        required: false,
      }),
    ],
  }),
  validation: null,
});

const fundWithCollateral = computed(() => fundingFM.form.initialMarginFundingType === FundingType.COLLATERAL);

const fundWithCredit = computed(() => fundingFM.form.initialMarginFundingType === FundingType.CREDIT);

const isFormValid = computed(
  () =>
    (fundWithCollateral.value && fundWithCollateralPossible.value) ||
    (fundWithCredit.value && fundWithCreditPossible.value) ||
    false
);

function reCheckCollaterals() {
  hasPostedCollateral.value = true;
  loadCollaterals();
}

function emitValues(creditContribution?: number, collateralContribution?: number) {
  emit('update:tradeFundingDetails', {
    initialMarginFundingType: fundingFM.form.initialMarginFundingType,
    initialMarginFundingCurrency: currency.value,
    initialMarginCollateralFundingAmount: collateralContribution,
    initialMarginCreditFundingAmount: creditContribution,
    valid: isFormValid.value,
  });
}

/**
 * Initial fund type set
 */
if (!fundWithCreditPossible.value) {
  fundingFM.form.initialMarginFundingType = FundingType.COLLATERAL;
  emitValues(0, props.tradePriceResponse.homeCurrencyInitialMarginAmount);
} else {
  fundingFM.form.initialMarginFundingType = FundingType.CREDIT;
  emitValues(props.tradePriceResponse.homeCurrencyInitialMarginAmount, 0);
}

function onFormEvent(event: FormEvent) {
  if (event.event === 'form-field-set-value') {
    if (fundWithCollateral.value) {
      emitValues(0, props.tradePriceResponse.homeCurrencyInitialMarginAmount);
    } else if (fundWithCredit.value) {
      emitValues(props.tradePriceResponse.homeCurrencyInitialMarginAmount, 0);
    } else {
      emitValues();
    }
  }
}

watch(
  () => props.tradeFundingDetails,
  () => {
    if (props.tradeFundingDetails) {
      fundingFM.form.initialMarginFundingType = props.tradeFundingDetails.initialMarginFundingType;
    }
  },
  { immediate: true }
);

watch(isFormValid, () => {
  if (props.tradeFundingDetails) {
    emit('update:tradeFundingDetails', { ...props.tradeFundingDetails, valid: isFormValid.value });
  }
});

return { __sfc: true,props, emit, fundingOptions, hasPostedCollateral, tradeState, onBehalfOf, loadCollaterals, collaterals, currency, areCollateralsLoading, sufficientCollateral, fundWithCollateralPossible, fundWithCreditPossible, fundingFM, fundWithCollateral, fundWithCredit, isFormValid, reCheckCollaterals, emitValues, onFormEvent, formatCurrencyValue, ValidatedForm, CollateralTransactionExecutor, TradeCollateralCreditFundForm }
}

})